import {
  createBrowserRouter,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router-dom';

import { FullPageError } from '@/components/full-page-error';

import { OrgLayout } from './layouts/org-layout';
import { PrivateLayout } from './layouts/private-layout';
import { RootLayout } from './layouts/root-layout';
import { SettingsLayout } from './layouts/settings-layout';
import { TenantLayout } from './layouts/tenant-layout';

const notFoundRoute = {
  path: '*',
  lazy: () => import('./pages/not-found'),
};

function RouteErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <FullPageError
        title="Page not found"
        message="The page you are looking for does not exist."
        onRetry={() => (window.location.href = '/')}
        retryText="Go Home"
      />
    );
  }

  return (
    <FullPageError
      title="Something went wrong"
      message="An unexpected error occurred. Please try refreshing the page."
      onRetry={() => window.location.reload()}
    />
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Auth routes (public)
      {
        path: '/auth/login',
        lazy: () => import('./pages/auth/login'),
      },
      {
        path: '/auth/callback',
        lazy: () => import('./pages/auth/callback'),
      },
      {
        path: '/auth/logout',
        lazy: () => import('./pages/auth/logout'),
      },
      // Invite route (public)
      {
        path: '/invite',
        lazy: () => import('./pages/invite'),
      },
      // Private routes
      {
        element: <PrivateLayout />,
        children: [
          // Root index — redirect page
          {
            index: true,
            lazy: () => import('./pages/index'),
          },
          // Org redirect
          {
            path: '/org',
            lazy: () => import('./pages/org/index'),
          },
          // Org layout routes
          {
            path: '/org/:slug',
            element: <OrgLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./pages/org/dashboard'),
              },
              {
                path: 'analytics',
                lazy: () => import('./pages/org/analytics'),
              },
              {
                path: 'data-completeness',
                lazy: () => import('./pages/org/data-completeness'),
              },
              {
                path: 'airports/:id',
                lazy: () => import('./pages/org/airports-detail'),
              },
              {
                path: 'applications',
                lazy: () => import('./pages/org/applications'),
              },
              {
                path: 'applications/:id',
                lazy: () => import('./pages/org/application-detail'),
              },
              {
                path: 'applications/:id/permits/:permitId',
                lazy: () => import('./pages/org/permit-detail'),
              },
              {
                path: 'applications/:id/submissions/:submissionId',
                lazy: () => import('./pages/org/submission-detail'),
              },
              {
                path: 'batch-operations',
                lazy: () => import('./pages/org/batch-operations'),
              },
              {
                path: 'batch-operations/:id',
                lazy: () => import('./pages/org/batch-operation-detail'),
              },
              {
                path: 'cargo',
                lazy: () => import('./pages/org/cargo'),
              },
              {
                path: 'charterers',
                lazy: () => import('./pages/org/charterers'),
              },
              {
                path: 'country',
                lazy: () => import('./pages/org/country'),
              },
              {
                path: 'country/:countryConfigId',
                lazy: () => import('./pages/org/country-detail'),
              },
              {
                path: 'crew',
                lazy: () => import('./pages/org/crew'),
              },
              {
                path: 'data',
                lazy: () => import('./pages/org/data'),
              },
              {
                path: 'fleet',
                lazy: () => import('./pages/org/fleet'),
              },
              {
                path: 'flight-list',
                lazy: () => import('./pages/org/flight-list'),
              },
              {
                path: 'flight-list/flights/:id',
                lazy: () => import('./pages/org/flight-detail'),
              },
              {
                path: 'flight-list/flight-groups/:id',
                lazy: () => import('./pages/org/flight-group-detail'),
              },
              {
                path: 'flight-list/import-history',
                lazy: () => import('./pages/org/import-history'),
              },
              {
                path: 'flight-list/import-history/:id',
                lazy: () => import('./pages/org/import-history-detail'),
              },
              {
                path: 'flight-list/destinations/:country/:airportName/:serviceType',
                lazy: () => import('./pages/org/destination-detail'),
              },
              {
                path: 'flight-list/overflight-countries/:country/:serviceType',
                lazy: () => import('./pages/org/overflight-detail'),
              },
              {
                path: 'organization',
                lazy: () => import('./pages/org/organization'),
              },
              {
                path: 'service-providers',
                lazy: () => import('./pages/org/service-providers'),
              },
              {
                path: 'settings',
                lazy: () =>
                  import('./pages/settings/redirect-to-system').then((m) => ({
                    Component: m.Component,
                  })),
              },
              {
                path: 'station-managers',
                lazy: () => import('./pages/org/station-managers'),
              },
              {
                path: 'station-managers/:id',
                lazy: () => import('./pages/org/station-manager-detail'),
              },
              {
                path: 'station-managers/:id/countries/:countryId',
                lazy: () => import('./pages/org/station-manager-country'),
              },
              {
                path: 'station-managers/:id/countries/:countryId/airports/:airportId',
                lazy: () => import('./pages/org/station-manager-airport'),
              },
              {
                path: 'tasks',
                lazy: () => import('./pages/org/tasks'),
              },
              {
                path: 'tasks/:id',
                lazy: () => import('./pages/org/task-detail'),
              },
              {
                path: 'users',
                lazy: () => import('./pages/org/users'),
              },
              notFoundRoute,
            ],
          },
          // Tenant layout routes
          {
            path: '/tenant',
            element: <TenantLayout />,
            children: [
              {
                index: true,
                lazy: () => import('./pages/tenant/dashboard'),
              },
              {
                path: 'organizations',
                lazy: () => import('./pages/tenant/organizations'),
              },
              {
                path: 'profile',
                lazy: () => import('./pages/tenant/profile'),
              },
              {
                path: 'roles',
                lazy: () => import('./pages/tenant/roles'),
              },
              {
                path: 'users',
                lazy: () => import('./pages/tenant/users'),
              },
              notFoundRoute,
            ],
          },
          // Settings layout routes
          {
            path: '/settings',
            element: <SettingsLayout />,
            children: [
              {
                index: true,
                lazy: () =>
                  import('./pages/settings/redirect').then((m) => ({
                    Component: m.Component,
                  })),
              },
              {
                path: 'profile',
                lazy: () => import('./pages/settings/user-settings'),
              },
              {
                // Legacy route redirect
                path: 'user',
                lazy: () =>
                  import('./pages/settings/redirect').then((m) => ({
                    Component: m.Component,
                  })),
              },
              {
                path: 'country-configs',
                lazy: () => import('./pages/settings/country-configs'),
              },
              {
                path: 'country-configs/:configId',
                lazy: () => import('./pages/settings/country-config-detail'),
              },
              {
                path: 'document-types',
                lazy: () => import('./pages/settings/document-types'),
              },
              // Org-scoped settings (require organization context)
              {
                path: 'org/:slug/system',
                lazy: () => import('./pages/settings/system'),
              },
              {
                path: 'org/:slug/display',
                lazy: () => import('./pages/settings/display'),
              },
              {
                path: 'org/:slug/notifications',
                lazy: () => import('./pages/settings/notifications'),
              },
              {
                path: 'org/:slug/integrations',
                lazy: () => import('./pages/settings/integrations'),
              },
              {
                path: 'org/:slug/default-data',
                lazy: () => import('./pages/settings/default-data'),
              },
              {
                path: 'org/:slug/event-types',
                lazy: () => import('./pages/settings/event-types'),
              },
              {
                path: 'org/:slug/service-types',
                lazy: () => import('./pages/settings/service-types'),
              },
              notFoundRoute,
            ],
          },
          notFoundRoute,
        ],
      },
    ],
  },
]);
